import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { AUTH_METHODS, AUTH_METHODS_OPTIONS } from '../constants';
import { DataSource, DataSourceSFTPConfig, DataSourceType } from '@/models/dataSource';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { Form, Formik, FormikProps } from 'formik';
import { getS3FilePreviewKey, useGetSFTPFilePreviewMutation } from '@/services/endpoints/files/filesApi';
import { updateFileData } from '@/features/fileUploading/fileUploadingSlice';
import { Input, Label, TextArea } from '@/lib';
import { Select } from '@/lib/Selects';
import { StepPageActions } from '@/global/components';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';

export type SFTPFormModel = Partial<DataSource<DataSourceSFTPConfig>>;

type NameAndBucketStepProps = {
  dataSourceType: DataSourceType;
  onNextStep: VoidFunction;
  onCancel: VoidFunction;
};

const CSV_ROWS_NUMBER = 5;

export const NameAndBucketStep: FC<NameAndBucketStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceSFTPConfig>> | undefined;
  const formRef = useRef<FormikProps<SFTPFormModel>>(null);

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      host: Yup.string().required(t('data_source_edit:validations.required')),
      username: Yup.string().required(t('data_source_edit:validations.required')),
      path: Yup.string().required(t('data_source_edit:validations.required')),
      authMethod: Yup.object({
        id: Yup.string()
          .required(t('data_source_edit:validations.required'))
          .oneOf(Object.values(AUTH_METHODS).map((method) => method)),
        label: Yup.string().required(),
      }).required(t('data_source_edit:validations.required')),
      password: Yup.string().when(['..authMethod.id'], {
        is: (id: string) => id === AUTH_METHODS.password,
        then: (schema) => schema.required(t('data_source_edit:validations.required')),
        otherwise: (schema) => schema.notRequired(),
      }),
      privateKey: Yup.string().when(['..authMethod.id'], {
        is: (id: string) => id === AUTH_METHODS.privateKey,
        then: (schema) => schema.required(t('data_source_edit:validations.required')),
        otherwise: (schema) => schema.notRequired(),
      }),
      passphrase: Yup.string(),
      port: Yup.number(),
    }),
  });

  const [getSFTPFilePreview, { isLoading }] = useGetSFTPFilePreviewMutation({
    fixedCacheKey: getS3FilePreviewKey,
  });

  const handleSubmit = async (form: SFTPFormModel) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { authMethod, ...rest } = form.configuration;
    const response = await getSFTPFilePreview({ ...rest, limit: CSV_ROWS_NUMBER });

    if ('data' in response) {
      const matrix = response.data.map((row) => row.split(','));

      dispatch(updateFileData({ preview: matrix?.slice(0, CSV_ROWS_NUMBER), header: matrix[0], isDefaultHeader: true }));
    }
  };

  const handleNextStep = async () => {
    await formRef.current?.submitForm();

    if (formRef.current?.isValid && formRef.current?.values) {
      dispatch(stashDataSource({ ...formRef.current.values, type: dataSourceType }));

      onNextStep();
    }
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={
        dataSource
          ? {
              ...dataSource,
              name: '',
            }
          : {
              name: '',
              configuration: {
                host: '',
                username: '',
                path: '',
                authMethod: undefined,
                password: undefined,
                privateKey: undefined,
                passphrase: undefined,
                port: undefined,
              },
            }
      }
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ values, errors, handleChange, setFieldError, setFieldValue }) => {
        return (
          <>
            <Form className='grow'>
              <Label>{t('data_source_edit:code.source_details.source_name')}</Label>
              <Input
                name='name'
                id='name'
                type='text'
                value={values?.name}
                placeholder={t('data_source_edit:sftp.source_name_placeholder')}
                onChange={(e) => {
                  setFieldError('name', undefined);
                  handleChange('name')(e.target.value);
                }}
                errorMsg={errors?.name}
              />

              <Label>{t('data_source_edit:sftp.host')}</Label>
              <Input
                name='configuration.host'
                id='configuration.host'
                type='text'
                value={values?.configuration.host}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.host', undefined);
                  handleChange('configuration.host')(e.target.value);
                }}
                errorMsg={errors?.configuration?.host}
              />

              <Label>{t('data_source_edit:sftp.username')}</Label>
              <Input
                name='configuration.username'
                id='configuration.username'
                type='text'
                value={values?.configuration.username}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.username', undefined);
                  handleChange('configuration.username')(e.target.value);
                }}
                errorMsg={errors?.configuration?.username}
              />

              <Label>{t('data_source_edit:sftp.path')}</Label>
              <Input
                name='configuration.path'
                id='configuration.path'
                type='text'
                value={values?.configuration.path}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.path', undefined);
                  handleChange('configuration.path')(e.target.value);
                }}
                errorMsg={errors?.configuration?.path}
              />
              <label className={'block py-2 text-start text-sm font-medium text-gray-700'}>{t('data_source_edit:sftp.passwordOrPrivateKey')}</label>
              <Select
                value={values.configuration.authMethod}
                items={AUTH_METHODS_OPTIONS.map((option) => ({
                  ...option,
                  label: t(`data_source_edit:sftp.${option.label as keyof typeof AUTH_METHODS}`),
                }))}
                onChange={(item) => {
                  setFieldValue('configuration.password', undefined);
                  setFieldValue('configuration.privateKey', undefined);
                  setFieldValue('configuration.authMethod', item);
                }}
                placeholder={t('data_source_edit:sftp.privateKey_placeholder')}
              />
              {values.configuration.authMethod?.id === AUTH_METHODS.password && (
                <>
                  <Label>{t('data_source_edit:sftp.password')}</Label>
                  <Input
                    name='configuration.password'
                    id='configuration.password'
                    type='text'
                    value={values?.configuration.password}
                    placeholder={t('data_source_edit:sftp.insertTextHere')}
                    onChange={(e) => {
                      setFieldError('configuration.password', undefined);
                      handleChange('configuration.password')(e.target.value);
                    }}
                    errorMsg={errors?.configuration?.password}
                  />
                </>
              )}
              {values.configuration.authMethod?.id === AUTH_METHODS.privateKey && (
                <>
                  <Label>{t('data_source_edit:sftp.privateKey')}</Label>
                  <TextArea
                    name='configuration.privateKey'
                    id='configuration.privateKey'
                    value={values?.configuration.privateKey}
                    placeholder={t('data_source_edit:sftp.insertTextHere')}
                    onChange={(e) => {
                      setFieldError('configuration.privateKey', undefined);
                      handleChange('configuration.privateKey')(e.target.value);
                    }}
                    errorMsg={errors?.configuration?.privateKey}
                    className='placeholder:whitespace-pre-wrap'
                    wrapperClassName='m-0 text-start'
                    maxLength={null}
                  />
                  <Label>{t('data_source_edit:sftp.passphrase')}</Label>
                  <Input
                    name='configuration.passphrase'
                    id='configuration.passphrase'
                    type='text'
                    value={values?.configuration.passphrase}
                    placeholder={t('data_source_edit:sftp.passphrase_placeholder')}
                    onChange={(e) => {
                      setFieldError('configuration.passphrase', undefined);
                      handleChange('configuration.passphrase')(e.target.value);
                    }}
                    errorMsg={errors?.configuration?.passphrase}
                  />
                </>
              )}
              <Label>{t('data_source_edit:sftp.port')}</Label>
              <Input
                name='configuration.port'
                id='configuration.port'
                type='number'
                value={values?.configuration.port}
                placeholder={t('data_source_edit:sftp.port_placeholder')}
                onChange={(e) => {
                  setFieldError('configuration.port', undefined);
                  handleChange('configuration.port')(e.target.value);
                }}
                errorMsg={errors?.configuration?.port}
              />
            </Form>
            <StepPageActions
              primaryButton={{
                text: 'COMMON.confirm',
                onClick: handleNextStep,
                isLoading,
              }}
              secondaryButton={{
                text: 'COMMON.cancel',
                onClick: onCancel,
              }}
            />
          </>
        );
      }}
    </Formik>
  );
};
